import React, { useState, useEffect, useRef } from 'react';
import { CheckSquare, Square, Camera, Loader2, X, ClipboardCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);

export const isAirbnbJob = (job) => job?.source === 'airbnb' || /airbnb/i.test(job?.service_type || '');

export const checklistComplete = (cfg, state) => {
  if (!cfg) return false;
  const checked = new Set(state?.checked || []);
  return cfg.items.every(i => checked.has(i.id)) && (state?.photos?.length || 0) >= (cfg.min_photos || 0);
};

const StaffAirbnbChecklist = ({ job, token, onChange }) => {
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState({ checked: job?.airbnb_checklist?.checked || [], photos: job?.airbnb_checklist?.photos || [] });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const readOnly = job?.status === 'completed';

  useEffect(() => { axios.get(`${API}/airbnb-checklist`).then(r => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { onChange?.(cfg, state); }, [cfg, state, onChange]);

  const persist = async (next) => {
    setState(next);
    setSaving(true);
    try { await axios.put(`${API}/staff/jobs/${job.id}/checklist`, next, { headers: { Authorization: `Bearer ${token}` } }); }
    catch (_) { /* keep local state */ }
    finally { setSaving(false); }
  };
  const toggle = (id) => {
    if (readOnly) return;
    const checked = state.checked.includes(id) ? state.checked.filter(x => x !== id) : [...state.checked, id];
    persist({ ...state, checked });
  };
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(async f => {
        const fd = new FormData(); fd.append('file', f);
        const r = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        return r.data.url;
      }));
      await persist({ ...state, photos: [...state.photos, ...urls] });
    } catch (_) { alert('Erro ao enviar foto / Photo upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const removePhoto = (u) => persist({ ...state, photos: state.photos.filter(p => p !== u) });

  if (!cfg) return null;
  const doneCount = cfg.items.filter(i => state.checked.includes(i.id)).length;
  const complete = checklistComplete(cfg, state);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-rose-200 p-6" data-testid="airbnb-checklist">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ClipboardCheck className="text-rose-500" size={20} /> Airbnb Checklist</h2>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${complete ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`} data-testid="airbnb-checklist-progress">
          {doneCount}/{cfg.items.length} · {state.photos.length}/{cfg.min_photos} 📷
        </span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-rose-500 transition-all" style={{ width: `${Math.round(((doneCount + Math.min(state.photos.length, cfg.min_photos)) / (cfg.items.length + cfg.min_photos)) * 100)}%` }} />
      </div>
      <ul className="space-y-2 mb-5">
        {cfg.items.map(item => {
          const on = state.checked.includes(item.id);
          return (
            <li key={item.id}>
              <button type="button" onClick={() => toggle(item.id)} disabled={readOnly} data-testid={`checklist-item-${item.id}`}
                className={`w-full flex items-center gap-3 text-left p-3 rounded-xl border transition-colors ${on ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200 hover:border-rose-300'} disabled:cursor-default`}>
                {on ? <CheckSquare className="text-green-600 flex-shrink-0" size={20} /> : <Square className="text-gray-400 flex-shrink-0" size={20} />}
                <span className={`text-sm ${on ? 'text-green-800 line-through' : 'text-gray-800'}`}>{item.label_pt}<span className="block text-xs text-gray-400 no-underline">{item.label_en}</span></span>
              </button>
            </li>
          );
        })}
      </ul>
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Fotos finais / Final photos (mín. {cfg.min_photos})</p>
        <div className="grid grid-cols-3 gap-2 mb-2">
          {state.photos.map(u => (
            <div key={u} className="relative aspect-square rounded-lg overflow-hidden border">
              <img src={absUrl(u)} alt="final" className="w-full h-full object-cover" />
              {!readOnly && <button type="button" onClick={() => removePhoto(u)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5" data-testid="checklist-photo-remove"><X size={12} /></button>}
            </div>
          ))}
          {!readOnly && (
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="checklist-photo-add"
              className="aspect-square rounded-lg border-2 border-dashed border-rose-300 text-rose-500 flex flex-col items-center justify-center gap-1 text-xs hover:bg-rose-50">
              {uploading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
              {uploading ? '...' : 'Adicionar'}
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden" onChange={upload} data-testid="checklist-photo-input" />
      </div>
      {saving && <p className="text-xs text-gray-400 mt-2">A guardar...</p>}
      {!complete && !readOnly && (
        <p className="text-xs text-rose-600 mt-3" data-testid="airbnb-checklist-warning">Complete todos os itens e as fotos para poder terminar. / Complete all items and photos to finish.</p>
      )}
    </div>
  );
};

export default StaffAirbnbChecklist;
